<script setup>
import AnButton from "@/components/common/AnButton.vue";
import NewsSection from "@/sections/dashboard/NewsSection.vue";
import PortfolioCharts from "@/sections/dashboard/PortfolioCharts.vue";
import PortfolioTable from "@/sections/dashboard/PortfolioTable.vue";
import { useUserStore } from "@/store/user-store";
import { computed } from "vue";
import GoalBanner from "@/sections/dashboard/GoalBanner.vue";
import Opportunities from "@/sections/dashboard/Opportunities.vue";
import DashboardGoalSection from "@/sections/dashboard/DashboardGoalSection.vue";
import AnLoader from "@/components/common/AnLoader.vue";
import { useRouter } from "vue-router";

// Define props
const props = defineProps({
  data: Object,
  isLoading: Boolean,
  showPortfolioCommentary: Boolean
});

// Define emits
const emit = defineEmits(['open-recommendation', 'refresh-dashboard', 'open-account-aggregator']);

const store = useUserStore();

const data = computed(() => props.data || store.getDashboardData || null);
const router = useRouter();

// Check if portfolio is effectively empty (all values are zero)
const isPortfolioEmpty = computed(() => {
  if (!data.value) return true;

  const summary = data.value.portfolioDailySummary?.[0];
  if (!summary) return true;

  return summary.portfolioTotalMarketVal === 0 &&
         summary.stockPFMarketVal === 0 &&
         summary.mutualFundPFMarketVal === 0 &&
         summary.otherTMV === 0;
});

// Navigate to add holdings screen
const navigateToAddHoldings = () => {
  router.push('/dashboard/add-holdings');
};
</script>

<template>
  <AnLoader v-if="store.isDashboardLoading" />
  <div v-else-if="!data">There is some issue</div>

  <!-- Show simple centered empty state for portfolios with no holdings OR all zero values -->
  <div
    v-else-if="(data?.stocksOwned?.length === 0 && (!data?.portfolioDailySummary || data?.portfolioDailySummary?.length === 0)) || isPortfolioEmpty"
    class="d-flex flex-direction-column align-items-center gap-20 flex-1 justify-center"
  >
    <img src="@/assets/images/no-data.png" class="image" />
    <div
      class="d-flex flex-direction-column align-items-center font-20 font-weight-500"
    >
      <span> Track all your investments here.</span>
      <span>Add your holdings to see real-time insights! </span>
      <div class="empty-state-actions">
        <AnButton label="Add Holding" variant="primary" @click="navigateToAddHoldings" />
        <button
          class="action-btn holdings-btn"
          @click="emit('open-account-aggregator')"
        >
          <i class="pi pi-download"></i>
          Get Existing Holdings
        </button>
        <button
          class="action-btn refresh-btn"
          @click="emit('refresh-dashboard')"
          :disabled="isLoading"
        >
          <i class="pi pi-refresh" :class="{ 'pi-spin': isLoading }"></i>
          Refresh Dashboard
        </button>
      </div>
    </div>
  </div>
  <div v-else>
    <!-- Only show charts if portfolio has meaningful data -->
    <PortfolioCharts
      v-if="!isPortfolioEmpty"
      :fixedIncome="data?.fixedIncome || []"
      :stocks="data?.stocksOwned || []"
      :mutualFunds="data?.mutualFunds || []"
      :otherInvestment="data?.otherInvest || []"
      :summary="data?.portfolioDailySummary || []"
    />

    <!-- Portfolio Header with Action Buttons - AFTER charts -->
    <div class="portfolio-header">
      <h2 class="portfolio-title">My Portfolio Securities</h2>
      <div class="portfolio-actions">
        <button
          class="action-btn refresh-btn"
          @click="emit('refresh-dashboard')"
          :disabled="isLoading"
        >
          <i class="pi pi-refresh" :class="{ 'pi-spin': isLoading }"></i>
          Refresh Dashboard
        </button>

        <button
          class="action-btn holdings-btn"
          @click="emit('open-account-aggregator')"
        >
          <i class="pi pi-download"></i>
          Get Existing Holdings
        </button>
      </div>
    </div>

    <PortfolioTable
      :stocks="data?.stocksOwned || []"
      :mutualFunds="data?.mutualFunds || []"
      :fixedIncome="data?.fixedIncome || []"
      :otherInvestments="data?.otherInvest || []"
      :dob="data?.dob || null"
      :portfolioSummary="data?.portfolioDailySummary?.[0] || null"
    />
    <!-- Only show other sections if portfolio has meaningful data -->
    <template v-if="!isPortfolioEmpty">
      <DashboardGoalSection />
      <GoalBanner :data="data" />
      <Opportunities />
      <NewsSection :news="Array.isArray(data.stockNews) ? data.stockNews : []" />
    </template>
  </div>
</template>

<style>
.image {
  height: 176px;
  width: 184px;
}

.empty-state-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.portfolio-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  margin-bottom: 20px;
  padding-top: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.portfolio-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.portfolio-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: 36px;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn:active {
  transform: translateY(0);
}

.action-btn i {
  font-size: 14px;
}

.refresh-btn {
  background: #10b981;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.refresh-btn:hover {
  background: #059669;
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.refresh-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.refresh-btn:disabled:hover {
  background: #9ca3af;
  transform: none;
  box-shadow: none;
}

.holdings-btn {
  background: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.holdings-btn:hover {
  background: #2563eb;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.commentary-btn {
  background: var(--an-primary, #0d7c68);
  box-shadow: 0 2px 4px rgba(13, 124, 104, 0.2);
}

.commentary-btn:hover {
  background: var(--an-primary-dark, #0a6454);
  box-shadow: 0 4px 8px rgba(13, 124, 104, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .portfolio-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .portfolio-actions {
    justify-content: center;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
